const millisecondsPerDay = 24 * 60 * 60 * 1000

/* Read one stable date object from a saved YYYY-MM-DD date value. */
export const read_leave_date_object = (dateValue) => {
    const safeDate = new Date(`${dateValue}T00:00:00.000Z`)

    if (Number.isNaN(safeDate.getTime())) {
        return null
    }

    return safeDate
}

/* Read one local calendar date from a saved leave date value. */
export const read_leave_local_date_object = (dateValue) => {
    const [datePart] = `${dateValue || ''}`.split('T')
    const [year, month, day] = datePart.split('-')
    const safeDate = new Date(Number(year), Number(month) - 1, Number(day))

    if (Number.isNaN(safeDate.getTime())) {
        return null
    }

    return safeDate
}

/* Read whether the first day of a leave request has already arrived. */
export const read_leave_request_has_started = (leaveRequest, asOfDate = new Date()) => {
    const startDate = read_leave_local_date_object(leaveRequest?.startDate)
    const todayDate = new Date(asOfDate)

    todayDate.setHours(0, 0, 0, 0)

    if (!startDate) {
        return true
    }

    return startDate.getTime() <= todayDate.getTime()
}

/* Read whether an approved employee leave covers the current working date. */
export const read_leave_request_is_active = (leaveRequest, asOfDate = new Date()) => {
    const approvedStatus = `${leaveRequest?.status || ''}`.trim().toLowerCase() === 'approved'
    const startDate = read_leave_local_date_object(leaveRequest?.startDate)
    const endDate = read_leave_local_date_object(leaveRequest?.endDate)
    const todayDate = new Date(asOfDate)

    todayDate.setHours(0, 0, 0, 0)

    if (!approvedStatus || !startDate || !endDate) {
        return false
    }

    const todayKey = [
        todayDate.getFullYear(),
        `${todayDate.getMonth() + 1}`.padStart(2, '0'),
        `${todayDate.getDate()}`.padStart(2, '0')
    ].join('-')
    const excludedDateKeys = (leaveRequest.excludedDates || []).map((dateValue) => {
        return `${dateValue || ''}`.split('T')[0]
    })
    const insideLeaveRange = startDate.getTime() <= todayDate.getTime()
        && endDate.getTime() >= todayDate.getTime()

    return insideLeaveRange && !excludedDateKeys.includes(todayKey)
}

/* Show King-registered leave immediately while regular future requests wait for their first day. */
export const read_leave_request_is_visible_in_history = (leaveRequest, asOfDate = new Date()) => {
    if (leaveRequest?.manualRegistration) {
        return true
    }

    return read_leave_request_has_started(leaveRequest, asOfDate)
}

/* Add full months to a date while preserving the calendar day where possible. */
export const add_leave_months = (dateValue, months) => {
    const nextDate = new Date(dateValue)

    nextDate.setUTCMonth(nextDate.getUTCMonth() + months)

    return nextDate
}

/* Read the first day of one calendar year. */
export const read_start_of_year = (year) => {
    return new Date(`${year}-01-01T00:00:00.000Z`)
}

/* Read the last day of one calendar year. */
export const read_end_of_year = (year) => {
    return new Date(`${year}-12-31T00:00:00.000Z`)
}

/* Read the earlier of two date objects. */
export const read_min_date = (firstDate, secondDate) => {
    if (firstDate < secondDate) {
        return firstDate
    }

    return secondDate
}

/* Read the later of two date objects. */
export const read_max_date = (firstDate, secondDate) => {
    if (firstDate > secondDate) {
        return firstDate
    }

    return secondDate
}

/* Count calendar days inclusively between two dates. */
export const read_inclusive_day_total = (startDate, endDate) => {
    const startUtc = Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate()
    )
    const endUtc = Date.UTC(
        endDate.getUTCFullYear(),
        endDate.getUTCMonth(),
        endDate.getUTCDate()
    )

    return Math.floor((endUtc - startUtc) / millisecondsPerDay) + 1
}

/* Count how many days exist in the supplied calendar year. */
export const read_days_in_year = (year) => {
    return read_inclusive_day_total(read_start_of_year(year), read_end_of_year(year))
}

/* Read whether a leave type subtracts from annual leave. */
export const read_leave_type_subtracts_from_annual = (leaveType) => {
    const normalizedLeaveType = `${leaveType || ''}`.trim().toLowerCase()
    const settings = {
        annual: true,
        personal: true,
        mass_day_off: true,
        medical: false,
        sick: false,
        unpaid: false
    }

    return Boolean(settings[normalizedLeaveType])
}

/* Read the calendar-year number of one employee for one target year. */
export const read_calendar_year_number = (startWorkingDate, targetYear) => {
    const startDate = read_leave_date_object(startWorkingDate)

    if (!startDate) {
        return 0
    }

    const startYear = startDate.getUTCFullYear()

    if (targetYear < startYear) {
        return 0
    }

    return targetYear - startYear + 1
}

/* Read the legal base-day total for the supported 5-day work week. */
export const read_base_days = (level) => {
    const baseDays = {
        firstYear: 20,
        after12Months: 21,
        after24Months: 22,
        after10SameOr12Total: 25,
        after25Total: 26
    }

    return baseDays[level] || 0
}

/* Read one prorated entitlement inside a target calendar year. */
export const read_prorated_entitlement = (annualDays, periodStart, periodEnd, targetYear) => {
    if (periodEnd < periodStart) {
        return 0
    }

    const activeDays = read_inclusive_day_total(periodStart, periodEnd)
    const totalDays = read_days_in_year(targetYear)

    return annualDays * activeDays / totalDays
}

/* Read the first monthly grant date that lands inside the active period. */
export const read_first_leave_grant_date = (periodStart) => {
    const grantDate = new Date(Date.UTC(
        periodStart.getUTCFullYear(),
        periodStart.getUTCMonth(),
        1
    ))

    if (grantDate < periodStart) {
        grantDate.setUTCMonth(grantDate.getUTCMonth() + 1)
    }

    return grantDate
}

/* Count month-start grants in the current year, with the final ceil applied after multiplication. */
export const read_monthly_accrued_entitlement = (annualDays, periodStart, periodEnd) => {
    if (periodEnd < periodStart) {
        return 0
    }

    const firstGrantDate = read_first_leave_grant_date(periodStart)

    if (periodEnd < firstGrantDate) {
        return 0
    }

    const yearDifference = periodEnd.getUTCFullYear() - firstGrantDate.getUTCFullYear()
    const monthDifference = periodEnd.getUTCMonth() - firstGrantDate.getUTCMonth()
    const activeMonths = yearDifference * 12 + monthDifference + 1

    return Math.ceil(activeMonths * annualDays / 12)
}

/* Read first-year senior days available until the January 1 refresh. */
export const read_first_year_experience_entitlement = (annualDays, startDate) => {
    const activeMonths = 11 - startDate.getUTCMonth()

    return Math.ceil(activeMonths * annualDays / 12)
}

/* Calculate the legal annual-leave entitlement earned by the selected date. */
export const read_legal_leave_entitlement = (startWorkingDate, asOfDate = new Date()) => {
    const startDate = read_leave_date_object(startWorkingDate)

    if (!startDate) {
        return 0
    }

    if (asOfDate < startDate) {
        return 0
    }

    const targetYear = asOfDate.getUTCFullYear()
    const calendarYearNumber = read_calendar_year_number(startWorkingDate, targetYear)
    const yearStart = read_start_of_year(targetYear)
    const yearEnd = read_end_of_year(targetYear)
    const calculationEnd = read_min_date(asOfDate, yearEnd)
    const sameEmployerYears = (asOfDate - startDate) / (365.25 * millisecondsPerDay)

    if (sameEmployerYears >= 25) {
        return read_base_days('after25Total')
    }

    if (sameEmployerYears >= 10) {
        return read_base_days('after10SameOr12Total')
    }

    if (calendarYearNumber === 1) {
        return read_monthly_accrued_entitlement(
            read_base_days('firstYear'),
            read_max_date(startDate, yearStart),
            calculationEnd
        )
    }

    if (calendarYearNumber === 2) {
        return read_monthly_accrued_entitlement(
            read_base_days('firstYear'),
            yearStart,
            calculationEnd
        )
    }

    if (calendarYearNumber >= 3) {
        const anniversary24Months = add_leave_months(startDate, 24)

        if (asOfDate >= anniversary24Months) {
            return read_base_days('after24Months')
        }

        return read_base_days('after12Months')
    }

    return 0
}

/* Read the 12-year total-experience leave entitlement for the current calendar year. */
export const read_twelve_years_experience_leave_entitlement = (startWorkingDate, asOfDate = new Date()) => {
    const startDate = read_leave_date_object(startWorkingDate)

    if (!startDate) {
        return 0
    }

    if (asOfDate < startDate) {
        return 0
    }

    const targetYear = asOfDate.getUTCFullYear()
    const startYear = startDate.getUTCFullYear()
    const annualEntitlement = read_base_days('after10SameOr12Total')

    if (startYear === targetYear) {
        return read_first_year_experience_entitlement(annualEntitlement, startDate)
    }

    return annualEntitlement
}

/* Read the legal leave entitlement for one saved team user and their seniority flags. */
export const read_user_legal_leave_entitlement = (user, asOfDate = new Date()) => {
    const startDate = user.startDate || user.startWorkingDate

    if (user.twelveYearsExperience) {
        return read_twelve_years_experience_leave_entitlement(startDate, asOfDate)
    }

    return read_legal_leave_entitlement(startDate, asOfDate)
}

/* Match one leave request to one saved team user. */
export const read_leave_request_belongs_to_user = (leaveRequest, user) => {
    const userKeys = [
        user.userId,
        user.id,
        user._id,
        user.email,
        user.username
    ].map((value) => `${value || ''}`.trim().toLowerCase()).filter(Boolean)
    const requestKeys = [
        leaveRequest.userId,
        leaveRequest.email,
        leaveRequest.username
    ].map((value) => `${value || ''}`.trim().toLowerCase()).filter(Boolean)

    return requestKeys.some((key) => userKeys.includes(key))
}

/* Read annual-leave requests for one user that overlap the selected year. */
export const read_matching_annual_leave_requests = (user, leaveRequests, asOfDate = new Date()) => {
    const targetYear = asOfDate.getUTCFullYear()
    const yearStart = `${targetYear}-01-01`
    const yearEnd = `${targetYear}-12-31`

    return (leaveRequests || []).filter((leaveRequest) => {
        const annualLeaveType = read_leave_type_subtracts_from_annual(leaveRequest.leaveType)
        const belongsToUser = read_leave_request_belongs_to_user(leaveRequest, user)
        const overlapsYear = (leaveRequest.startDate || '') <= yearEnd && (leaveRequest.endDate || '') >= yearStart

        return annualLeaveType && belongsToUser && overlapsYear
    })
}

/* Read approved annual leave days that already reduce the yearly balance. */
export const read_approved_annual_leave_days = (user, leaveRequests, asOfDate = new Date()) => {
    return read_matching_annual_leave_requests(user, leaveRequests, asOfDate).filter((leaveRequest) => {
        const approvedStatus = `${leaveRequest.status || ''}`.trim().toLowerCase() === 'approved'

        return approvedStatus
    }).reduce((dayTotal, leaveRequest) => {
        return dayTotal + Number(leaveRequest.countedDays || 0)
    }, 0)
}

/* Read pending annual leave days that should reserve balance immediately. */
export const read_pending_annual_leave_days = (user, leaveRequests, asOfDate = new Date()) => {
    const pendingStatuses = ['department_head_review', 'company_head_review', 'team_lead_review', 'pending_team_lead']

    return read_matching_annual_leave_requests(user, leaveRequests, asOfDate).filter((leaveRequest) => {
        const pendingStatus = pendingStatuses.includes(`${leaveRequest.status || ''}`.trim().toLowerCase())

        return pendingStatus
    }).reduce((dayTotal, leaveRequest) => {
        return dayTotal + Number(leaveRequest.countedDays || 0)
    }, 0)
}

/* Read future approved annual leave days that should appear inside Accepted. */
export const read_accepted_annual_leave_days = (user, leaveRequests, asOfDate = new Date()) => {
    const todayStart = new Date(asOfDate)

    todayStart.setHours(0, 0, 0, 0)

    return read_matching_annual_leave_requests(user, leaveRequests, asOfDate).filter((leaveRequest) => {
        const approvedStatus = `${leaveRequest.status || ''}`.trim().toLowerCase() === 'approved'
        const startDate = read_leave_date_object(leaveRequest.startDate)
        const futureStartDate = startDate && startDate.getTime() > todayStart.getTime()

        return approvedStatus && futureStartDate
    }).reduce((dayTotal, leaveRequest) => {
        return dayTotal + Number(leaveRequest.countedDays || 0)
    }, 0)
}

/* Read current available leave days from legal entitlement, adjustments, approved leaves, and pending requests. */
export const read_available_leave_days = (
    user,
    leaveRequests = [],
    leaveDaysAdjustment = 0,
    asOfDate = new Date()
) => {
    const legalEntitlement = Math.ceil(read_user_legal_leave_entitlement(user, asOfDate))
    const approvedAnnualLeaveDays = read_approved_annual_leave_days(user, leaveRequests, asOfDate)
    const pendingAnnualLeaveDays = read_pending_annual_leave_days(user, leaveRequests, asOfDate)

    return legalEntitlement + Number(leaveDaysAdjustment || 0) - approvedAnnualLeaveDays - pendingAnnualLeaveDays
}
